'use client';

import { Button } from '@components/ui/button';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger
} from '@components/ui/drawer';

import { ArrowRight } from 'lucide-react';

import { Input } from '@components/ui/input';
import { Textarea } from '@components/ui/textarea';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { requestBetaAccess } from '@actions/request-beta-access';

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@components/ui/form';

import { Alert, AlertDescription } from '@components/ui/alert';
import { AlertCircle, CheckCircle } from 'lucide-react';

// Form schema
const formSchema = z.object({
  email: z
    .string({ error: 'Email is required' })
    .min(1, { message: 'Email is required' })
    .email({ message: 'Invalid email address format' }),
  company: z.string().optional()
});

type FormValues = z.infer<typeof formSchema>;

export function RequestAccessDrawer() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formResponse, setFormResponse] = useState<{
    success: boolean;
    message: string;
    rateLimited?: boolean;
    retryAfter?: number | null;
  } | null>(null);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const rateLimitTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Define form
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      company: ''
    }
  });

  // Handle rate limiting (silently)
  useEffect(() => {
    if (formResponse?.rateLimited && formResponse.retryAfter) {
      const now = Date.now();
      const retryAfter = formResponse.retryAfter;

      if (retryAfter > now) {
        setIsRateLimited(true);

        if (rateLimitTimeoutRef.current) {
          clearTimeout(rateLimitTimeoutRef.current);
        }

        rateLimitTimeoutRef.current = setTimeout(() => {
          setIsRateLimited(false);
        }, retryAfter - now);
      }
    }

    return () => {
      if (rateLimitTimeoutRef.current) {
        clearTimeout(rateLimitTimeoutRef.current);
      }
    };
  }, [formResponse]);

  // Handle form submission
  async function onSubmit(data: FormValues) {
    setIsSubmitting(true);
    setFormResponse(null);

    try {
      // Create FormData object
      const formData = new FormData();
      formData.append('email', data.email);
      formData.append('company', data.company || '');

      // Call server action
      const response = await requestBetaAccess(null, formData);
      setFormResponse(response);

      // Reset form on success
      if (response.success) {
        form.reset();
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setFormResponse({
        success: false,
        message: 'An unexpected error occurred. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  }
  return (
    <Drawer>
      <DrawerTrigger asChild>
        <Button>
          Request Access
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      </DrawerTrigger>
      <DrawerContent className="dark:bg-black">
        <div className="mx-auto w-full max-w-sm ">
          <DrawerHeader>
            <DrawerTitle>Request Private Beta Access</DrawerTitle>
            <DrawerDescription>
              Join our exclusive beta program and be among the first to
              experience Lqd platform! 🚀
            </DrawerDescription>
          </DrawerHeader>

          <div className="p-4 pb-0">
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-6"
              >
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter your email"
                          {...field}
                          disabled={isSubmitting || isRateLimited}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="company"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Tell us a few words about your company
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Optional: Share details about your company and how you plan to use our product"
                          className="resize-y min-h-[100px]"
                          {...field}
                          disabled={isSubmitting || isRateLimited}
                        />
                      </FormControl>
                      <FormDescription>
                        This information helps us understand your needs better.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {formResponse && (
                  <Alert
                    variant={formResponse.success ? 'default' : 'destructive'}
                  >
                    <div className="flex items-center gap-2">
                      {formResponse.success ? (
                        <CheckCircle className="h-4 w-4" />
                      ) : (
                        <AlertCircle className="h-4 w-4" />
                      )}
                      <AlertDescription>
                        {formResponse.message}
                      </AlertDescription>
                    </div>
                  </Alert>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting || isRateLimited}
                  className="w-full"
                >
                  {isSubmitting ? 'Processing...' : 'Request Access'}
                </Button>
              </form>
            </Form>
          </div>

          <DrawerFooter>
            <DrawerClose asChild>
              <Button variant="outline" className="w-full">
                Cancel
              </Button>
            </DrawerClose>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
